/**
 * Contratti di aggiornamento per code (call_queues) e ring group
 * (ring_groups) — Task #4701.
 *
 * Unica fonte per BE (validazione body + elenco chiuso delle colonne
 * aggiornabili) e FE (tipo del payload e proiezione del form). Ogni campo
 * corrisponde 1:1 a una colonna reale della tabella (schema.sql): il service
 * itera SOLO su questo elenco, mai sulle chiavi del body, e lo schema è
 * `.strict()` → una chiave sconosciuta produce 400, mai SQL.
 *
 * Escluse di proposito: `id`, `tenant_id`, `created_at`, `updated_at`
 * (identità/audit), `current_calls` (contatore runtime), `agents` /
 * `members` (SoT = tabelle `queue_agents` / `ring_group_members`, gestite
 * dalle sub-risorse `/agents` e `/members`).
 */
import { z } from 'zod';

const optionalText = (max: number) => z.string().max(max).nullable().optional();
const optionalInt = (min = 0, max = 2_147_483_647) => z.number().int().min(min).max(max).optional();
const optionalBool = z.boolean().optional();
const optionalJson = z.record(z.unknown()).nullable().optional();

export const callQueueStrategySchema = z.enum([
  'ring-all',
  'longest-idle',
  'round-robin',
  'top-down',
  'agent-with-least-talk-time',
]);
export type CallQueueStrategy = z.infer<typeof callQueueStrategySchema>;

export const callQueueTimeoutActionSchema = z.enum(['hangup', 'voicemail', 'forward']);
export const callQueueTimeBaseScoreSchema = z.enum(['system', 'queue', 'member']);

export const callQueueUpdateFieldsSchema = z.object({
  store_id: z.string().uuid().nullable().optional(),
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().nullable().optional(),
  extension: z.string().trim().min(1).max(10).optional(),
  strategy: callQueueStrategySchema.optional(),
  max_wait_time: optionalInt(),
  max_wait_time_with_no_agent: optionalInt(),
  max_wait_time_with_no_agent_time_reached: optionalInt(),
  tier_rules_apply: optionalBool,
  tier_rule_wait_second: optionalInt(),
  tier_rule_wait_multiply_level: optionalBool,
  tier_rule_no_agent_no_wait: optionalBool,
  discard_abandoned_after: optionalInt(),
  abandoned_resume_allowed: optionalBool,
  moh_sound: optionalText(255),
  record_template: optionalText(255),
  time_base_score: callQueueTimeBaseScoreSchema.optional(),
  max_penalty: optionalInt(),
  min_penalty: optionalInt(),
  queue_timeout: optionalInt(),
  queue_timeout_action: callQueueTimeoutActionSchema.optional(),
  queue_timeout_destination: optionalText(50),
  failover_enabled: optionalBool,
  failover_destination_type: optionalText(50),
  failover_destination_id: z.string().uuid().nullable().optional(),
  failover_destination_data: optionalJson,
  caller_id_name: optionalText(100),
  caller_id_number: optionalText(50),
  recording_enabled: optionalBool,
  recording_path: optionalText(255),
  recording_consent_required: optionalBool,
  max_concurrent_calls: optionalInt(1),
  settings: z.record(z.unknown()).optional(),
  enabled: optionalBool,
  callback_enabled: optionalBool,
  callback_max_wait_seconds: optionalInt(),
  callback_min_position: optionalInt(),
  callback_max_requests: optionalInt(),
  callback_announcement: optionalText(255),
}).strict();

/** Elenco chiuso (tipizzato) dei campi/colonne aggiornabili di `call_queues`. */
export const CALL_QUEUE_UPDATE_FIELDS = callQueueUpdateFieldsSchema.keyof().options;
export type CallQueueUpdateField = (typeof CALL_QUEUE_UPDATE_FIELDS)[number];

const hasAtLeastOneField = (value: Record<string, unknown>) =>
  Object.values(value).some((v) => v !== undefined);
const NO_FIELDS_MESSAGE = 'Nessun campo da aggiornare';

export const callQueueUpdateBodySchema = callQueueUpdateFieldsSchema.refine(hasAtLeastOneField, {
  message: NO_FIELDS_MESSAGE,
});
export type CallQueueUpdateBody = z.infer<typeof callQueueUpdateBodySchema>;

/** Agente passato alla creazione coda (solo `extension_id` obbligatorio; il resto segue i default del service). */
export const callQueueAgentInputSchema = z.object({
  extension_id: z.string().uuid(),
  agent_name: z.string().max(100).nullish(),
  agent_type: z.enum(['callback', 'uuid-standby', 'uuid-bridge']).optional(),
  contact: z.string().max(255).nullish(),
  tier_level: z.number().int().min(1).max(10).optional(),
  tier_position: z.number().int().min(1).max(100).optional(),
  enabled: z.boolean().optional(),
}).passthrough();
export type CallQueueAgentInput = z.infer<typeof callQueueAgentInputSchema>;

/**
 * Body di creazione coda (route tenant `POST /voip/queues`): `name` ed
 * `extension` obbligatori, resto opzionale. Passthrough: le chiavi extra che il
 * client rimanda (id, tenant_id, current_calls, …) sono ignorate dal service,
 * che scrive un elenco esplicito di colonne.
 */
export const callQueueCreateBodySchema = callQueueUpdateFieldsSchema
  .extend({
    name: z.string().trim().min(1).max(100),
    extension: z.string().trim().min(1).max(10),
    agents: z.array(callQueueAgentInputSchema).optional(),
  })
  .passthrough();
export type CallQueueCreateBody = z.infer<typeof callQueueCreateBodySchema>;

/** Sub-risorsa agenti e stato coda (route tenant). */
export const callQueueAddAgentBodySchema = z.object({ extension_id: z.string().uuid() }).passthrough();
export const callQueueAgentTierBodySchema = z.object({
  tier_level: z.coerce.number().int(),
  tier_position: z.coerce.number().int(),
}).passthrough();
export const callQueueAgentStatusBodySchema = z.object({
  status: z.string().min(1).max(32),
  tier_level: z.number().int().min(1).max(10).optional(),
}).passthrough();
export const callQueuePauseBodySchema = z.object({ paused: z.boolean() }).passthrough();
export type CallQueuePauseBody = z.infer<typeof callQueuePauseBodySchema>;

export const ringGroupStrategySchema = z.enum(['ringall', 'hunt', 'random', 'simultaneous']);
export type RingGroupStrategy = z.infer<typeof ringGroupStrategySchema>;
export const ringGroupTimeoutActionSchema = z.enum(['voicemail', 'hangup', 'forward']);

export const ringGroupUpdateFieldsSchema = z.object({
  store_id: z.string().uuid().nullable().optional(),
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().nullable().optional(),
  extension: z.string().trim().min(1).max(10).optional(),
  strategy: ringGroupStrategySchema.optional(),
  ring_time: optionalInt(1),
  member_settings: z.record(z.unknown()).optional(),
  moh_sound: optionalText(255),
  voicemail_enabled: optionalBool,
  voicemail_extension: optionalText(10),
  voicemail_password: optionalText(20),
  voicemail_email: optionalText(255),
  call_timeout: optionalInt(),
  call_timeout_action: ringGroupTimeoutActionSchema.optional(),
  call_timeout_destination: optionalText(50),
  failover_enabled: optionalBool,
  failover_destination_type: optionalText(50),
  failover_destination_id: z.string().uuid().nullable().optional(),
  failover_destination_data: optionalJson,
  caller_id_name: optionalText(100),
  caller_id_number: optionalText(50),
  recording_enabled: optionalBool,
  recording_path: optionalText(255),
  recording_consent_required: optionalBool,
  max_concurrent_calls: optionalInt(1),
  settings: z.record(z.unknown()).optional(),
  enabled: optionalBool,
  skip_busy_members: optionalBool,
  skip_dnd_members: optionalBool,
  confirm_call: optionalBool,
}).strict();

/** Elenco chiuso (tipizzato) dei campi/colonne aggiornabili di `ring_groups`. */
export const RING_GROUP_UPDATE_FIELDS = ringGroupUpdateFieldsSchema.keyof().options;
export type RingGroupUpdateField = (typeof RING_GROUP_UPDATE_FIELDS)[number];

export const ringGroupUpdateBodySchema = ringGroupUpdateFieldsSchema.refine(hasAtLeastOneField, {
  message: NO_FIELDS_MESSAGE,
});
export type RingGroupUpdateBody = z.infer<typeof ringGroupUpdateBodySchema>;

function pickFields<K extends string>(source: object, fields: readonly K[]): Partial<Record<K, unknown>> {
  const record = source as Record<string, unknown>;
  const out: Partial<Record<K, unknown>> = {};
  for (const field of fields) {
    if (record[field] !== undefined) out[field] = record[field];
  }
  return out;
}

/**
 * Proietta un oggetto più largo (es. la riga completa restituita dalla GET,
 * usata come stato del form) sui soli campi aggiornabili e lo valida.
 * Lancia ZodError se un valore non rispetta il contratto.
 */
export function toCallQueueUpdateBody(source: object): CallQueueUpdateBody {
  return callQueueUpdateBodySchema.parse(pickFields(source, CALL_QUEUE_UPDATE_FIELDS));
}

export function toRingGroupUpdateBody(source: object): RingGroupUpdateBody {
  return ringGroupUpdateBodySchema.parse(pickFields(source, RING_GROUP_UPDATE_FIELDS));
}

/** `POST /voip/queues/agents/reset-on-break` → `data`: agenti riportati a Available. */
export const queueAgentsResetOnBreakResultSchema = z.object({
  reset: z.number(),
  agents: z.array(z.string()),
});
export type QueueAgentsResetOnBreakResult = z.infer<typeof queueAgentsResetOnBreakResultSchema>;
